import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { HttpClient } from '@angular/common/http';
import { PrequalModel, UserProfileModel } from './models/prequal.model';
import { ApiService } from './api.service';
import { WpAjaxService } from '../wp-ajax/wp-ajax.service';
import { IAutoLoanSettings, IAdverseData, IAdversePdfData, IEndpointConfig } from './interfaces/auto-loans.interface';
import { CookieHelperService } from '../cookie-helper.service';
import { SessionHelperService } from '../session-helper.service';
import { IPrequalifiedResponse, IPrequalifyResponse, IPrequalResponse } from '../../../api/intefaces/prequal.interface';
import { Subscriber } from 'rxjs';
import { PDFGeneratorService } from './pdf-generator.service';
import { SettingsService } from './settings.service';
import { IUpdateOfferResponse, IUpdateOfferUIRequest, IUpdateOfferUIResponse } from './interfaces/update-offer.interface';
import { UpdateOfferModel } from './models/update-offer.model';
import { VehicleFilterModel } from './models/vehicle-filter.model';
import { ActivatedRoute } from '@angular/router';

@Injectable()
export class AutoLoanService extends ApiService {

  urlParams: any = null;

  get enableOptimized (): boolean {
    return  this._settingService.optimizedEnabled;
  }

  get settings (): IAutoLoanSettings {
    return this._settingService.settings;
  }

  get endpointConfig (): IEndpointConfig {
    return this._settingService.endpointConfig;
  }

  get activeStates (): string[] {
    return this.settings && this.settings.activeStates ? this.settings.activeStates : [
      'ca',
    ];
  }
  
  get isDemo (): boolean {
    return this.urlParams && this.urlParams.demo !== undefined;
}

  constructor(
    protected _http: HttpClient,
    protected _route: ActivatedRoute,
    protected _session: SessionHelperService,
    protected _settingService: SettingsService,
    protected _pdfService: PDFGeneratorService,
    protected _cookieService: CookieHelperService,
    protected _wpAjax: WpAjaxService
    ) {
    super(_http);
    this._route.queryParams.subscribe(params => {
      this.urlParams = params;
    });
  }

  endpointGenerator (endpoint: string): string
  {
    let url: string = `/nowcom-wfs-direct/service/${this.endpointConfig.protocol}/${this.endpointConfig.domain}`;
    return `${url}/${endpoint}`;
  }

  uploadAAN (adverseData, dealId?: any, title?: string): Observable<any> {
    return new Observable(observer => {
      const { uploadAAN } = this.endpointConfig.methods;
      if (uploadAAN && uploadAAN.endpoint) {
        const options: any = {
          headers: {
            'Content-Type': 'application/json'
          }
        };
        if (uploadAAN.security)
        {
          options.headers['Authorization'] = `Basic ${uploadAAN.security}`;
        }
  
        const adversePayload: any = {
          DealId: dealId,
          StipulationTypeId: 42,
          FileName: `${title}.pdf`,
          DocumentData: adverseData,
        };
  
        this.postRaw(
          this.endpointGenerator(uploadAAN.endpoint),
          adversePayload,
          false,
          options
        ).subscribe(
          response => {
            observer.next(response);
            observer.complete();
          },
          error => {
            observer.error(error);
            observer.complete();
          }
        );
        return;
      }
      observer.error(null);
      observer.complete();
    });
  }

  //#region - Prequalify
  prequalify (data: any): Observable<IPrequalifyResponse> {
    if (this.enableOptimized) {
      return new Observable<IPrequalifyResponse>(observer => {
        const payload = new PrequalModel(data);
        const userProfile: UserProfileModel = payload.get('UserProfile');

        // Active States //
        if (this.activeStates.indexOf(userProfile.get('state', '').toLowerCase()) === -1 && !this.isDemo) {
          // Comming soon page //
          return observer.next({
            success: true,
            data: {
              redirectUrl: this._settingService.settings.outOfStateUrl
            }
          });
        }

        // Generate New Session //
        this._session.startSession().subscribe(
          (sessionGuid: string) => {
            this._cookieService._setCookie('MarketingConsent', userProfile.get('marketingConsent', false));
            this._cookieService._setCookie('PrivacyPolicyConsent', userProfile.get('privacyPolicyConsent', false));
          
            // Visited Page //
            if (payload.get('dealId')) {
              payload.get('UserTrackingInfo').set('VisitedPage', this.settings.visitedPage);
            }

            this.postRaw(
              this.endpointGenerator(this.endpointConfig.methods.prequalify),
              payload.transform(),
              false,
              {
                headers: {
                  'Content-type': 'application/json',
                  'Authorization': `${this._session.CzAuthToken}`
                }
              }
            ).subscribe(
              (response: IPrequalResponse) => {
                this.handlePrequalResponse(observer, sessionGuid, response, payload, data);
              },
              error => {
                observer.error(error);
                observer.complete();
              }
            );
          },
          error => {
            observer.error(error);
          }
        )
      });
    }
    return this._wpAjax.post_request(data);
  }

  // Prequal Response Handling //
  handlePrequalResponse (
    observer: Subscriber<IPrequalifyResponse>,
    sessionGuid: string,
    response: IPrequalResponse,
    payload: PrequalModel,
    data: any
  ) {
    const userProfile: UserProfileModel = payload.get('UserProfile');
    // Set Required Session //
    this._session.setSessions(
      [{
        key: 'PrequalResponse',
        value: response
      }, {
        key: 'ApplicationDetails',
        value: payload.transform()
      }],
      sessionGuid
    ).subscribe(() => {
      if (response.IsPreQual) {
        observer.next({
          success: true,
          data: <IPrequalifiedResponse> {
            SignerResult: response.SignerResult,
            WDSession: sessionGuid,
            approvalId: response.ApprovalId,
            aprrovalDate: payload.get('PreQualApproval').get('ApprovalDate', null),
            expirationDate: payload.get('PreQualApproval').get('VoucherExpDate', null),
            dealId: response.DealId,
            initialOffers: response,
            isPrequal: response.IsPreQual,
            leadId: response.VelocifyId,
            sessionId: response.SessionId,
            svcerrorMessage: response.SVCErrorMessage,
            userProfile: {
              dateOfBirth: userProfile.get('DOB'),
              lastName: userProfile.get('lastName'),
              firstName: userProfile.get('firstName')
            }
          }
        });
        observer.complete();
        return;
      }

      // Check DealId //
      if (!response.DealId) {
        // Throw empty application id error //
        observer.error({
          success: false,
          error_code: 500,
          error_data: response,
          error_message: 'Empty Application ID.'
        });
        observer.complete();
      } else {
        this.handleTurndown(observer, sessionGuid, data, payload, response);
      }
    });
  }

  // Turndown Handling //
  handleTurndown (observer: Subscriber<IPrequalifyResponse>, sessionGuid: string, data: any, payload: PrequalModel, response: IPrequalResponse) {
    const turndownUrl = this._settingService.settings.turndownUrl;
    const adverseData: IAdverseData = {
      WDSession: sessionGuid,
      dealId: response.DealId,
      redirectUrl: turndownUrl,
      isPrequal: response.IsPreQual,
      velocifyID: response.VelocifyId,
      FicoFactors: response.FicoFactors,
      MaximumScore: response.MaximumScore,
      MinumumScore: response.MinumumScore,
      adverseActionReasons: response.AdverseActionReasons,
    };

    // Formulate Adverse PDF //
    const adversePdfData: any = Object.assign(
      data,
      <IAdversePdfData> {
        AdverseActionReasons: adverseData.adverseActionReasons,
        creditKeyFactors: adverseData.FicoFactors,
        customer_address: data.address,
        customer_city: data.city,
        customer_state: data.state,
        customer_zip: data.zip,
        dealId: response.DealId,
        maximumScore: response.MaximumScore,
        minumumScore: response.MinumumScore,
        primaryScore: response.FicoScore,
        userProfile: {
          firstName: data.firstName,
          lastName: data.lastName
        }
      }
    );
    // Check Vehicle //
    if (payload.get('VehicleItem', null)) {
      // Push Vehicle Details //
      adversePdfData.vehicleDetails = payload.get('VehicleItem').transform();
    }

    // Generate Adverse PDF //
    this._pdfService.generate(adversePdfData)
      .subscribe(
        pdfResult => {
          this._session.setSessions([{
            key: 'AdversePDFData',
            value: adversePdfData
          }, {
            key: 'AdversePDFBase64',
            value: pdfResult.data
          }], sessionGuid).subscribe(
            sessionResult => {
              // Upload adverse pdf //
              this.uploadAAN(
                  pdfResult.data,
                  response.DealId,
                  `Adverse Action-${data.firstName}-${data.lastName}`
              ).subscribe(
                  response => {
                    // Submit Adverse Notification Mail //
                    observer.next({
                      success: true,
                      data: adverseData
                    });
                  },
                  error => {
                    // Submit Adverse Notification Mail //
                    observer.next({
                      success: true,
                      data: adverseData
                    });
                  }
              );
            },
            error => {

            }
          );
        },
        error => {}
      );
  }
  //#endregion

  //#region - Update Offer
  updateOffer (data: any): Observable<IUpdateOfferUIResponse> {
    if (this.enableOptimized) {
      return new Observable<IUpdateOfferUIResponse> (observer => {
        const request: IUpdateOfferUIRequest = data;
        const payload = new UpdateOfferModel(request);
        this.postRaw(
          this.endpointGenerator(this.endpointConfig.methods.updateOffer),
          payload.transform(),
          false,
          {
            headers: {
              'Content-type': 'application/json',
              'Authorization': `${this._session.CzAuthToken}`
            }
          }
        ).subscribe(
          (response: IUpdateOfferResponse) => {
            // Handle //
            observer.next({
              success: true,
              data: response
            });
            observer.complete();
          },
          error => {
            observer.error(error);
            observer.complete();
          }
        );
      });
    }
    return this._wpAjax.post_request(data);
  }
  //#endregion

  //#region - Get User Details
  getUserDetails (data: any): Observable<any> {
    if (this.enableOptimized) {
      return new Observable(observer => {
        this.postRaw(
          this.endpointGenerator(this.endpointConfig.methods.getUserDetails),
          data,
          false,
          {
            headers: {
              'Content-type': 'application/json',
              'Authorization': `${this._session.CzAuthToken}`
            }
          }
        ).subscribe(
          response => {
            observer.next({
              success: true,
              data: response
            });
            observer.complete();
          },
          error => {
            observer.error({
              success: false,
              data: error.error
            });
            observer.complete();
          }
        );
      })
    }
    return this._wpAjax.post_request(data);
  }
  //#endregion

  //#region - Get Closest Dealers
  getClosestDealers (data: any): Observable<any> {
    if (this.enableOptimized) {
      return new Observable(observer => {
        let payload = Object.assign(
          {
            Distance: 150,
            IsDemo: false
          },
          data
        );
        const dealerFilters = JSON.parse(payload.dealershipFilter);
        payload = {
          IsDemo: false,
          DealId: dealerFilters.dealId,
          DealershipFilter: {
            Street: dealerFilters.street,
            State: dealerFilters.state,
            City: dealerFilters.city,
            Zip: dealerFilters.zip,
            VehicleMake: dealerFilters.vehicleMake
          }
        };
        this.postRaw(
          this.endpointGenerator(this.endpointConfig.methods.closestDealers),
          payload,
          false,
          {
            headers: {
              'Content-type': 'application/json',
              'Authorization': `${this._session.CzAuthToken}`
            }
          }
        ).subscribe(
          response => {
            observer.next({
              success: true,
              data: response
            });
            observer.complete();
          },
          error => {
            observer.error({
              success: false,
              data: error.error
            });
            observer.complete();
          }
        );
      });
    }
    return this._wpAjax.post_request(data);
  }
  //#endregion

  //#region - Get Vehicles
  getVehicles (data: any): Observable<any> {
    if (this.enableOptimized) {
      return new Observable(observer => {
        const payload = new VehicleFilterModel(data);
        this.postRaw(
          this.endpointGenerator(this.endpointConfig.methods.getVehicles),
          payload.transform(),
          false,
          {
            headers: {
              'Content-type': 'application/json',
              'Authorization': `${this._session.CzAuthToken}`
            }
          }
        ).subscribe(
          response => {
            observer.next({
              success: true,
              data: response
            });
            observer.complete();
          },
          error => {
            observer.error({
              success: false,
              data: error.error
            });
            observer.complete();
          }
        );
      });
    }
    return this._wpAjax.post_request(data);
  }
  //#endregion

  //#region - Vehicles YMM
  getClassicYears (): Observable<any> {
    return this.postRaw(
      this.endpointGenerator(this.endpointConfig.methods.classicYears),
      {
        WCCPartnerServiceCompanyName: 'LoanCenter'
      }
    );
  }
  getClassicMakes (year: number): Observable<any> {
    return this.postRaw(
      this.endpointGenerator(this.endpointConfig.methods.classicMakes),
      {
        Year: year,
        WCCPartnerServiceCompanyName: 'LoanCenter'
      }
    );
  }
  getClassicModels (year: number, make: string): Observable<any> {
    return this.postRaw(
      this.endpointGenerator(this.endpointConfig.methods.classicModels),
      {
        Make: make,
        Year: year,
        WCCPartnerServiceCompanyName: 'LoanCenter'
      }
    );
  }
  //#endregion

}



// WEBPACK FOOTER //
// ./src/app/shared/services/auto-loans/auto-loan.service.ts